import React, { useState, useEffect, useRef } from 'react';
import Header from '../Header';
import SideBar from '../SideBar';
import './HistoryReport.scss';
import { useParams } from 'react-router-dom';
import { Button, Table, Form, FormControl, Modal, FormLabel } from 'react-bootstrap';
import { saveAs } from 'file-saver';
import { CSVLink } from 'react-csv';
// import { PDFExport } from '@progress/kendo-react-pdf';

// auth imports
import jwt_decode from 'jwt-decode';
import { serverUrl } from '../../../serverUrl';
import { toast } from 'react-toastify';

const buildSearchString = (receipt) => (
    [
        receipt?.invoice_id,
        receipt?.receipt_id,
        receipt?.tenant_name,
        receipt?.paid_by,
        receipt?.month,
        receipt?.year,
        receipt?.description,
        receipt?.date,
        receipt?.date_paid,
        receipt?.amount_paid,
        receipt?.withholding_tax_amount,
        receipt?.settled_total,
        receipt?.has_withholding_tax ? 'withholding tax' : null,
        receipt?.balance_due,
    ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
);

const getReceiptSortValue = (receipt) => {
    if (typeof receipt?.sort_order === 'number') {
        return receipt.sort_order;
    }

    const parsedDate = Date.parse(receipt?.date_paid || receipt?.date || '');
    return Number.isNaN(parsedDate) ? 0 : parsedDate;
};

function ReceiptReport({ tenantUserId }) {
    // sidebar and header functions
    const [showSidebar, setShowSidebar] = useState(true);
    function toggleSidebar() {
        setShowSidebar(!showSidebar);
    }

    // getting the id and finding the apparment details
    const { id } = useParams();
    // house selected
    const [houseSelected, setHouseSelected] = useState([]);
    const [houseSelectedReport, setHouseSelectedReport] = useState([]);
    const [houseSelectedReportPaid, setHouseSelectedReportPaid] = useState([]);
    // loading status
    const [isLoading, setIsLoading] = useState(false);

    // get the list of the houses

    const accessToken = localStorage.getItem('authTokens');
    // get the access token
    const parsedTokens = JSON.parse(accessToken);
    const access = parsedTokens.access;

    // headers access token
    const config = {
        headers: { Authorization: `Bearer ${access}` },
    };
    // decoding the token so that i can get the user id
    const decodedToken = jwt_decode(accessToken);
    const userId = decodedToken.user_id;

    // get the house selected by user
    useEffect(() => {
        setIsLoading(true);
        serverUrl
            .get(`/landlord/house/${id}/`, config)
            .then((res) => {
                setHouseSelected(res.data);
                setIsLoading(false);
            })
            .catch((error) => {
                console.log(error);
                setIsLoading(false);
            });
    }, [id]);

    useEffect(() => {
        if (tenantUserId === undefined) {
            return;
        }

        if (!tenantUserId) {
            setHouseSelectedReport([]);
            return;
        }

        const params = `house_id=${id}&user_id=${tenantUserId}`;
        serverUrl.get(`/tenant/house-tenant-receipts/?${params}`, config)
            .then((res) => {
                setHouseSelectedReport(Array.isArray(res.data?.results) ? res.data.results : []);
            })
            .catch((error) => {
                console.log(error);
                setHouseSelectedReport([]);
            });
    }, [id, tenantUserId])

    // declare the selected house
    const selectedUnit = houseSelected;
    const idHouse = parseInt(id);

    async function handleDownload(invoiceId) {
        setIsLoading(true);
        try {
            const res = await serverUrl.get(
                `/tenant/generate_pdf_receipt?invoice_id=${invoiceId}&download=true`,
                { ...config, responseType: 'blob', timeout: 45000 }
            );
            const downloadUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', 'receipt_report.pdf');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
            toast.success('Successfully Downloaded Your Receipt Report');
        } catch (error) {
            console.log(error);
            toast.error('Unable to download the receipt PDF.');
        } finally {
            setIsLoading(false);
        }
    }

    async function handleDownloadAll() {
        try {
            if (!tenantUserId) {
                toast.error('Select a tenant to download receipt reports.');
                return;
            }
            setIsLoading(true);
            const params = `house_id=${id}&user_id=${tenantUserId}`;
            const res = await serverUrl.get(
                `/tenant/generate_pdf_receipt?${params}&download=true`,
                { ...config, responseType: 'blob', timeout: 45000 }
            );
            const downloadUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', 'house_receipt_report.pdf');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
            toast.success('Successfully Downloaded Your House Receipt Report');
        } catch (error) {
            console.log(error);
            toast.error('Unable to download the house receipt report.');
        } finally {
            setIsLoading(false);
        }
    }

    const handleViewPDF = async (invoiceId) => {
        const previewWindow = window.open('', '_blank');
        setIsLoading(true);
        try {
            const res = await serverUrl.get(
                `/tenant/generate_pdf_receipt?invoice_id=${invoiceId}&download=true`,
                { ...config, responseType: 'blob', timeout: 45000 }
            );
            const pdfUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            if (previewWindow && !previewWindow.closed) {
                previewWindow.location.href = pdfUrl;
            }
        } catch (error) {
            if (previewWindow && !previewWindow.closed) previewWindow.close();
            console.log(error);
            toast.error('Unable to open the receipt PDF.');
        } finally {
            setIsLoading(false);
        }
    };

    function getMonthNumber(month) {
        const months = {
            january: 1,
            february: 2,
            march: 3,
            april: 4,
            may: 5,
            june: 6,
            july: 7,
            august: 8,
            september: 9,
            october: 10,
            november: 11,
            december: 12
        };

        return months[month];
    }
    // searching function
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedRow, setSelectedRow] = useState(null);

    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
        setSelectedRow(null); // Reset selected row when user starts a new search
    };
    const filteredData = [...houseSelectedReport]
        .filter((item) => buildSearchString(item).includes(searchQuery.toLowerCase()))
        .sort((a, b) => getReceiptSortValue(b) - getReceiptSortValue(a));

    const handleRowClick = (item) => {
        setSelectedRow(item);
    };

    // design the table to display 10 each per table
    const [currentPage, setCurrentPage] = useState(0);
    const itemsPerPage = 5;

    useEffect(() => {
        setCurrentPage(0);
    }, [searchQuery, tenantUserId, houseSelectedReport]);

    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
    const startIndex = currentPage * itemsPerPage;
    const endIndex = currentPage === totalPages - 1 ? filteredData.length : startIndex + itemsPerPage;
    const displayedItems = filteredData.slice(startIndex, endIndex);
    const rangeStart = filteredData.length === 0 ? 0 : startIndex + 1;
    const rangeEnd = filteredData.length === 0 ? 0 : Math.min(endIndex, filteredData.length);


    const handlePrevPage = () => {
        setCurrentPage(currentPage - 1);
    };

    const handleNextPage = () => {
        setCurrentPage(currentPage + 1);
    };

    const exportCSV = () => {
        const csvData = [
            ['Cash Paid', 'Withholding Tax', 'Total Settled', 'Month', 'Year', 'Date Paid', 'Item'],
            ...houseSelectedReport.map((item) => [
                item.amount_paid || item.total,
                item.has_withholding_tax ? item.withholding_tax_amount : '-',
                item.settled_total || item.total,
                item.month,
                item.year,
                item.date_paid,
                item.description,
            ]),
        ];
        const csvBlob = new Blob([csvData.join('\n')], { type: 'text/csv;charset=utf-8;' });
        saveAs(csvBlob, 'data.csv');
    };

    const pdfExportRef = useRef();

    const exportPDF = () => {
        pdfExportRef.current.save();
    };

    const [editModal, setEditModal] = useState(false)
    const [editAmount, setEditAmount] = useState(null)
    const [receiptId, setReceiptId] = useState(null)
    const [transactionId, setTransactionId] = useState(null)

    const handleOpenEdit = (receipt, transaction) => {
        if (!receipt) {
            console.error('Receipt ID is undefined');
            return;
        }
        if (!transaction) {
            console.error('Transaction ID is undefined');
            return;
        }

        setEditModal(true);
        setReceiptId(receipt);
        setTransactionId(transaction)

        // Get the details of the invoice
        serverUrl
            .get(`/landlord/c2btranscation/${receipt}/`, config)
            .then((res) => {
                setEditAmount(parseInt(res.data.transcation_amount))
            })
            .catch((error) => {
                console.error('Error fetching transaction details:', error);
            });
    };


    const handleCloseEdit = () => {
        setEditModal(false)
    }
    function handleEditPayment(e) {
        e.preventDefault()
        const data = {
            'transcation_amount': editAmount
        }
        const transactionData = {
            'amount': editAmount
        }
        serverUrl
            .patch(`landlord/c2btranscation/${receiptId}/`, data, config)
            .then((res) => {
                // update transaction
                serverUrl.patch(`/landlord/transaction/${transactionId}/`, transactionData, config)
                    .then((res) => {
                        // Fetch updated data from the server
                        if (!tenantUserId) {
                            setHouseSelectedReport([]);
                            return;
                        }
                        const refreshParams = `house_id=${id}&user_id=${tenantUserId}`;
                        serverUrl.get(`/tenant/house-tenant-receipts/?${refreshParams}`, config)
                            .then((response) => {
                                setHouseSelectedReport(response.data.results);
                                toast.success('Successfully edited the amount');
                                setEditModal(false);
                                setEditAmount(null);
                            })
                            .catch((fetchError) => {
                                console.error('Error fetching updated data:', fetchError);
                            });

                    })
                    .catch((error) => {
                        console.log(error)
                    })

            })
            .catch((error) => {
                console.error(error);
            });

    }
    const [selectedReceipt, setSelectedReceipt] = useState(null);
    const [showReceiptModal, setShowReceiptModal] = useState(false);

    const handleViewDetails = (receipt) => {
        setSelectedReceipt(receipt);
        setShowReceiptModal(true);
    };
    if (isLoading) {
        return (
            <div className="loader-container">
                <div className="loader"></div>
            </div>
        );
    }
    return (
        <>
            {selectedUnit.id === idHouse ? (
                <div className="HistoryReport" atyle={{ width: "105vw", fontSize: "8px" }}>
                    {/* list of the tenants of the apartment and payment details */}
                    <div className="tasks ">
                        <h2>Receipt report - {selectedUnit.house_number} </h2>
                        {/* <p>
            Current Status :
            {selectedUnit.occupancy_status === 'occupied' ? (
              <b className="text-success text-uppercase"> {selectedUnit.occupancy_status}</b>
            ) : (
              <b className="text-danger text-uppercase">{selectedUnit.occupancy_status}</b>
            )}
          </p> */}
                        <div className="d-flex justify-content-between">
                            <div>
                                <Button className="btn btn-success text-white me-3" onClick={exportCSV}>
                                    {' '}
                                    EXCEL <i className="bi bi-file-earmark-excel "></i>
                                </Button>
                                <Button className="btn btn-danger text-white" onClick={handleDownloadAll}>
                                    PDF <i className="bi bi-file-pdf-fill"></i>
                                </Button>
                            </div>
                            <Form className="searchBar">
                                <FormControl type="text" placeholder="Search" onChange={handleSearch} />
                            </Form>
                        </div>
                        {/* <Table className="table mt-3">
                            <thead className="">
                                <tr>
                                    <th onClick={() => handleSort('invoice_id')}>
                                        Invoice
                                    </th>
                                    <th onClick={() => handleSort('amount')}>
                                        Paid
                                    </th>
                                      <th onClick={() => handleSort('paid_by')}>
                                        Paid By
                                    </th>
                                      <th onClick={() => handleSort('receipt_id')}>
                                        Transaction ID
                                    </th>
                                    <th onClick={() => handleSort('overflow_amount')}>
                                        Overpaid
                                    </th>
                                    <th onClick={() => handleSort('status')}>
                                        Month
                                    </th>
                                    <th onClick={() => handleSort('amount')}>
                                        Year
                                    </th>
                                    <th onClick={() => handleSort('amount')}>
                                        Date
                                    </th>
                                    <th onClick={() => handleSort('days')}>
                                        Item
                                    </th>
                                    <th onClick={() => handleSort('added_by_name')}>
                                        Added By
                                    </th>
                                    <th onClick={() => handleSort('date')}>
                                        Receipt
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {displayedItems.map((houseHistory) => (
                                    <tr
                                        key={houseHistory.id}
                                        onClick={userId === houseHistory.user && houseHistory.ctob_id ? () => handleOpenEdit(houseHistory.ctob_id, houseHistory.transaction_id) : null}

                                    >
                                        <td>INV {houseHistory.invoice_id}</td>
                                        <td
                                            className={userId === houseHistory.user ? 'text-decoration-underline text-primary' : ''}
                                            style={userId === houseHistory.user ? { cursor: 'pointer' } : {}}

                                        >{houseHistory.total}</td>
                                        <td className='text-capitalize'>{houseHistory.paid_by}</td>
                                        <td className='text-uppercase'>{houseHistory.receipt_id}</td>
                                        <td>{houseHistory.overflow_amount}</td>
                                        <td>{houseHistory.month}</td>
                                        <td>{houseHistory.year}</td>
                                        <td>{houseHistory.date}</td>
                                        <td>{houseHistory.description}</td>
                                        <td className='text-capitalize'>{houseHistory.added_by_name}</td>
                                        <td> <Button className="btn btn-primary text-white btn-sm" onClick={() => handleViewPDF(houseHistory.invoice_id)}>
className                                            View <i ="bi bi-eye-fill"></i>
                                        </Button></td>

                                        <td>
                                            <Button className="btn btn-danger text-white btn-sm" onClick={() => handleDownload(houseHistory.invoice_id)}>
                                                PDF <i className="bi bi-file-pdf-fill"></i>
                                            </Button></td>
                                    </tr>
                                ))}
                            </tbody>
                            <div className="bottomDataHouseReport">
                            </div>
                        </Table> */}


                        <div className="bg-white rounded-lg mt-4 overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Invoice
                                            </th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Amount
                                            </th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Paid By
                                            </th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Receipt
                                            </th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Date Paid
                                            </th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Invoice Month/Year
                                            </th>

                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {displayedItems.length === 0 ? (
                                            <tr>
                                                <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                                                    {searchQuery ? "No matching receipts found" : "No receipts available"}
                                                </td>
                                            </tr>
                                        ) : (
                                            displayedItems.map((receipt) => (
                                                <tr key={receipt.id} className="hover:bg-gray-50">
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                        INV {receipt.invoice_id}
                                                    </td>
                                                    <td
                                                        className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${userId === receipt.user ? 'underline text-primary cursor-pointer' : ''
                                                            }`}
                                                    >
                                                        {receipt.total}
                                                        {receipt.has_withholding_tax && (
                                                            <span className="block mt-1 text-xs font-medium text-amber-700 normal-case no-underline">
                                                                Includes withholding tax: {receipt.withholding_tax_amount}
                                                            </span>
                                                        )}
                                                    </td>

                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                                                        {receipt.paid_by}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 uppercase">
                                                        {receipt.receipt_id || '-'}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        {receipt.date || receipt.date_paid || '-'}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        {receipt.month} {receipt.year}
                                                    </td>

                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                        <div className="flex space-x-2">
                                                            <button
                                                                onClick={() => handleViewDetails(receipt)}
                                                                className="text-white hover:text-white bg-blue-600 hover:bg-blue-700 px-3 py-1 rounded-md transition-colors"
                                                            >
                                                                View Details
                                                            </button>
                                                            <button
                                                                onClick={() => handleDownload(receipt.invoice_id)}
                                                                className="text-white hover:text-white bg-red-500 hover:bg-red-600 px-3 py-1 rounded-md transition-colors"
                                                            >
                                                                Download PDF
                                                            </button>
                                                            <button
                                                                onClick={() => handleViewPDF(receipt.invoice_id)}
                                                                className="text-white hover:text-white bg-red-600 hover:bg-red-700 px-3 py-1 rounded-md transition-colors"
                                                            >
                                                                View PDF
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="pagination">
                            <button onClick={handlePrevPage} disabled={currentPage === 0 || totalPages === 0}>
                                &lt;
                            </button>
                            <span>{`${rangeStart}-${rangeEnd} of ${filteredData.length}`}</span>
                            <button onClick={handleNextPage} disabled={totalPages === 0 || currentPage >= totalPages - 1}>
                                &gt;
                            </button>
                        </div>
                    </div>


                    {/* Receipt Detail Modal */}
                    {showReceiptModal && selectedReceipt && (
                        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                            <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
                                <div className="flex justify-between items-center pb-3 border-b">
                                    <h3 className="text-xl font-semibold text-gray-800">Receipt Details</h3>
                                    <button onClick={() => setShowReceiptModal(false)} className="text-gray-400 hover:text-gray-500">
                                        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>

                                <div className="mt-4 space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <p className="text-sm font-medium text-gray-500">Invoice ID</p>
                                            <p className="text-sm text-gray-900">INV {selectedReceipt.invoice_id}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-gray-500">
                                                {selectedReceipt.has_withholding_tax ? 'Cash Paid' : 'Amount'}
                                            </p>
                                            <p className="text-sm text-gray-900 text-uppercase">{selectedReceipt.total?.toLocaleString('en-us')}</p>
                                        </div>
                                        {selectedReceipt.has_withholding_tax && (
                                            <>
                                                <div>
                                                    <p className="text-sm font-medium text-gray-500">Withholding Tax</p>
                                                    <p className="text-sm font-semibold text-amber-700 uppercase">
                                                        {selectedReceipt.withholding_tax_amount}
                                                    </p>
                                                </div>
                                                <div>
                                                    <p className="text-sm font-medium text-gray-500">Total Settled</p>
                                                    <p className="text-sm text-gray-900 uppercase">{selectedReceipt.settled_total}</p>
                                                </div>
                                            </>
                                        )}
                                        <div>
                                            <p className="text-sm font-medium text-gray-500">Paid By</p>
                                            <p className="text-sm text-gray-900 capitalize">{selectedReceipt.paid_by}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-gray-500">Transaction ID</p>
                                            <p className="text-sm text-gray-900 uppercase">{selectedReceipt.receipt_id}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-gray-500">Overpaid Amount</p>
                                            <p className="text-sm text-gray-900">KES. {selectedReceipt.overflow_amount?.toLocaleString('en-us') || '0'}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-gray-500">Invoice Month/Year</p>
                                            <p className="text-sm text-gray-900">{selectedReceipt.month} {selectedReceipt.year}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-gray-500">Date Paid</p>
                                            <p className="text-sm text-gray-900">{selectedReceipt.date}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-gray-500">Added By</p>
                                            <p className="text-sm text-gray-900 capitalize">{selectedReceipt.added_by_name}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-gray-500">Balance Due</p>
                                            <p className="text-sm text-gray-900 uppercase">{selectedReceipt.balance_due}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-gray-500">Payment Status</p>
                                            <p className="text-sm text-gray-900 capitalize">{selectedReceipt.payment_status}</p>
                                        </div>
                                    </div>

                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Description</p>
                                        <p className="text-sm text-gray-900">{selectedReceipt.description}</p>
                                    </div>
                                </div>

                                <div className="mt-6 flex justify-end space-x-3">
                                    <button
                                        onClick={() => handleDownload(selectedReceipt.invoice_id)}
                                        className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                                    >
                                        Download PDF
                                    </button>
                                    <button
                                        onClick={() => setShowReceiptModal(false)}
                                        className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}


                    {/* modal for editing the amount */}
                    <Modal show={editModal} onHide={handleCloseEdit}>
                        <Modal.Header closeButton>
                            <h2>Edit Added Payment</h2>
                        </Modal.Header>
                        <Modal.Body>
                            <Form onSubmit={handleEditPayment}>
                                <div>
                                    <div className="placeholder-text mt-3 mb-2" style={{ fontSize: '0.9rem' }}>Edit Amount</div>
                                    <input type='number' className="input-field" onChange={(e) => setEditAmount(e.target.value)} value={editAmount}
                                    />
                                </div>


                                <Button className='loginBtn mt-3 ms-4' type='submit'>Save</Button>
                            </Form>
                        </Modal.Body>

                    </Modal>

                </div >
            ) : (
                <></>
            )
            }
        </>
    )
}

export default ReceiptReport



